import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Role, User } from 'app/_models';
import { DialogsService } from 'app/_services/dialogs/dialogs.service';
import { NotificationService } from 'app/_services/notification.service';
import { UserRolesService } from 'app/_services/users-roles.service';
import { EditRoleDialogComponent } from '../edit-role-dialog/edit-role-dialog.component';

@Component({
    selector: 'app-roles',
    templateUrl: './roles.component.html',
    styleUrls: ['../../shared/table-defaults.scss', './roles.component.scss'],
})
export class RolesComponent implements OnInit {
    value = '';
    users: User[];
    roles: Role[];
    rolesNames: string[];
    dataSource: MatTableDataSource<Role>;
    selection = new SelectionModel<Role>(true, []);
    removeItems = false;

    displayedColumns: string[] = ['label', 'totalusers', 'actions'];
    columnsToDisplay = this.displayedColumns.slice();

    @ViewChild(MatTable) table: MatTable<Role[]>;
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        public dialog: MatDialog,
        private dialogService: DialogsService,
        private userRolesSerivce: UserRolesService,
        private notificationService: NotificationService
    ) {}

    ngOnInit(): void {
        this.userRolesSerivce.rolesDataSource.subscribe((roles) => {
            this.loadDatasource(roles);
        });
    }

    loadDatasource(data: Role[]): void {
        this.roles = data;
        this.dataSource = new MatTableDataSource(data);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        this.rolesNames = this.dataSource.data.map((role) => role.roleName);
    }

    applyFilter(searchTerms: any): void {
        this.dataSource.filter = searchTerms;

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    clearFilter(): void {
        this.dataSource.filter = '';

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    openEditRoleDialog(role: Role, title: string, creating = false): MatDialogRef<EditRoleDialogComponent> {
        const roles = this.roles;
        const rolesNames = this.rolesNames;
        const config: MatDialogConfig = {
            width: '450px',
            panelClass: 'edit-role',
            data: { creating, role, roles, rolesNames, title },
        };

        return this.dialog.open(EditRoleDialogComponent, config);
    }

    addRole(): void {
        const roles = this.roles;
        const creating = true;
        const title = 'Add New Role';
        const newRole: Role = {
            roleId: 0, // Set to 0 since column is set to Identity in database
            roleName: '',
        };

        const dialogResult = this.openEditRoleDialog(newRole, title, creating);

        dialogResult.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            this.userRolesSerivce.createRole(result).subscribe((res: Role) => {
                roles.push(res);
                this.loadDatasource(this.roles);
                this.userRolesSerivce.getUsers();
                this.notificationService.showSuccess(`New Role has been added.`);
            });
        });
    }

    editRole(role: Role) {
        const title = 'Edit Role';

        if (role.roleName === 'Admin') {
            return this.notificationService.showError(`Admin role cannot be editted.`);
        }

        const dialogResult = this.openEditRoleDialog(role, title);

        dialogResult.afterClosed().subscribe((result: Role) => {
            if (!result) {
                return;
            }

            this.userRolesSerivce.updateRole(result.roleId, result).subscribe((_) => {
                this.userRolesSerivce.getUsers();
                this.notificationService.showSuccess(`Role has been updated.`);
            });
        });
    }

    deleteRole(role: Role): void {
        let submessage = '';
        if (role.roleName === 'Admin' || role.roleName === 'Contributor') {
            return this.notificationService.showError(`${role.roleName} role cannot be editted.`);
        }

        if (role.userRole.length && role.userRole.length > 0) {
            submessage = `Deleting this role will remove it from at least ${role.userRole.length} user(s) and default them to the Contributor role`;
        }

        const dialogResult$ = this.dialogService.OpenConfirmationDialog({
            TitleText: 'Delete Roles',
            MessageText: 'Are you sure you want to delete this role?',
            SubMessageText: submessage,
            OKText: 'Yes, remove role',
            CancelText: 'No',
            DelayDelete: 3, // Number of seconds to delay delete button
        });

        dialogResult$.subscribe((result) => {
            if (!result) {
                return;
            }

            const index = this.dataSource.data.indexOf(role, 0);

            if (index > -1) {
                this.dataSource.data.splice(index, 1);
            }

            this.userRolesSerivce.deleteRole(role.roleId).subscribe((_) => {
                this.loadDatasource(this.roles);
                this.userRolesSerivce.getRoles();
                this.userRolesSerivce.getUsers();
                this.notificationService.showSuccess(`Role have been removed.`);
            });
        });
    }
}
